import { useState } from "react";
import { Link } from "react-router-dom";
import { Popover, styled } from "@monolith-forensics/monolith-ui";
import { HelpCircleIcon } from "lucide-react";
import { DismissableBanner } from "@/Features/_components/DismissableBanner";
import { useFeatures } from "@/contexts/FeaturesContext";
import { useCaseFiles } from "@/Features/Files/Hooks/useCaseFiles";
import { FILE_SOURCE_TYPES } from "@/Features/Files/files.types";
import {
  EVIDENCE_FILES_ROOT_BANNER_ID,
  EVIDENCE_ITEM_FOLDER_BANNER_ID,
} from "./evidence-files.constants";
import { useEvidenceItemSummary } from "./evidence-files.query";
import { evidenceItemFilesPath, isManagedFolder } from "./evidence-files.utils";
import { LearnMoreLink, ManagedTooltip } from "./ManagedRowIcon";

// DismissableBanner renders nothing once closed, so the spacing goes on it
// rather than on a wrapper that would stay behind.
const StyledTreeBanner = styled(DismissableBanner)`
  margin-bottom: 10px;
`;

// Shared by the root banner and the (?) on the Evidence Files crumb, which keeps
// it reachable once the banner is closed (ENG-2251).
const ROOT_TITLE = "The Evidence Files folder is managed by Monolith";
const ROOT_MESSAGE =
  "Each evidence item gets a folder holding its files. Some actions are limited here. Don't see an item? Add its first file from the item's Files tab.";

const RootBanner = () => (
  <StyledTreeBanner
    id={EVIDENCE_FILES_ROOT_BANNER_ID}
    title={ROOT_TITLE}
    data-testid="evidence-files-root-banner"
    actions={<LearnMoreLink />}
  >
    {ROOT_MESSAGE}
  </StyledTreeBanner>
);

const ItemFolderBanner: React.FC<{
  folder: { name: string; source_id?: number | null };
  caseId: number;
}> = ({ folder, caseId }) => {
  const { data: item } = useEvidenceItemSummary(folder.source_id);
  // The folder is named by the evidence number (ENG-2131); the lookup wins
  // once it's back, in case the item was renumbered since.
  const evidenceNumber = item?.evidence_number ?? folder.name;

  return (
    <StyledTreeBanner
      id={EVIDENCE_ITEM_FOLDER_BANNER_ID}
      title={`These are evidence item ${evidenceNumber}'s files, including photos`}
      data-testid="evidence-item-folder-banner"
      actions={
        <>
          {item && (
            <Link
              to={evidenceItemFilesPath(item.uuid, caseId)}
              data-testid="evidence-item-folder-banner-item-link"
            >
              See in evidence item
            </Link>
          )}
          <LearnMoreLink />
        </>
      }
    >
      You can also manage them in the item's Files tab, and images show in its
      Photos tab too. A change in one place shows up in the others.
    </StyledTreeBanner>
  );
};

/**
 * The info banner above the Case Files table inside the managed Evidence
 * Files tree (ENG-2133): one at the root, one in each item folder, each
 * dismissable under its own key. Nothing anywhere else, or with
 * evidence_files_in_case_files off.
 */
export const EvidenceFilesTreeBanner: React.FC = () => {
  const { currentFolder, case_id } = useCaseFiles();
  const { hasFeatures, FEATURES } = useFeatures();

  if (!hasFeatures([FEATURES.EVIDENCE_FILES_IN_CASE_FILES])) return null;

  if (currentFolder?.source_type === FILE_SOURCE_TYPES.EVIDENCE_ROOT_FOLDER) {
    return <RootBanner />;
  }
  if (currentFolder?.source_type === FILE_SOURCE_TYPES.EVIDENCE_ITEM_FOLDER) {
    // Keyed by folder so moving between item folders resets the lookup.
    return (
      <ItemFolderBanner
        key={currentFolder.uuid}
        folder={currentFolder}
        caseId={case_id}
      />
    );
  }
  return null;
};

// The crumb and its (?) together are the hover target.
const StyledTreeHintTarget = styled.span`
  display: inline-flex;
  align-items: center;
`;

const StyledTreeHint = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  border-radius: 50%;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:hover,
  &[aria-expanded="true"] {
    background-color: ${({ theme }) => theme.palette.action.hover};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const HINT_LABEL = "About the Evidence Files folder";

const StyledTreeHintCopy = styled.div`
  font-size: 12px;
`;

/**
 * The Evidence Files breadcrumb with a (?) after it, anywhere in the managed
 * tree (ENG-2251): hovering the crumb or the (?) shows the root banner's copy
 * and Learn more, so it's still reachable after the banner is closed. A click
 * or tap on the (?) opens it too, since a hover never fires on a tap. Just the
 * crumb elsewhere, or with the flag off.
 */
export const EvidenceFilesTreeHint: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentFolder } = useCaseFiles();
  const { hasFeatures, FEATURES } = useFeatures();
  const [opened, setOpened] = useState(false);

  if (
    !hasFeatures([FEATURES.EVIDENCE_FILES_IN_CASE_FILES]) ||
    !isManagedFolder(currentFolder)
  ) {
    return <>{children}</>;
  }

  return (
    <Popover
      trigger="hover"
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      closeDelay={200}
    >
      <Popover.Target>
        <StyledTreeHintTarget data-testid="evidence-files-crumb">
          {children}
          {/* Opens only: a hover already opened it, so a toggle would close it. */}
          <StyledTreeHint
            type="button"
            aria-label={HINT_LABEL}
            aria-expanded={opened}
            aria-haspopup="dialog"
            data-testid="evidence-files-tree-hint"
            onClick={() => setOpened(true)}
          >
            <HelpCircleIcon size={14} />
          </StyledTreeHint>
        </StyledTreeHintTarget>
      </Popover.Target>
      <Popover.Dropdown aria-label={HINT_LABEL}>
        <StyledTreeHintCopy data-testid="evidence-files-tree-hint-copy">
          <ManagedTooltip>
            {ROOT_TITLE}. {ROOT_MESSAGE}
          </ManagedTooltip>
        </StyledTreeHintCopy>
      </Popover.Dropdown>
    </Popover>
  );
};
